import type { CSSProperties } from "react";
import type { BundlesData } from "@/lib/cms";
import { BUNDLE_GUIDANCE } from "../BundleMatrix";
import { ModuleIcon } from "../ModuleIcon";
import { FieldError } from "./fields";

type Bundle = BundlesData["bundles"][number];

/** Signature mark per tier. Autonomous carries The Wolf, its defining module. */
const TIER_MARKS: Record<string, string> = { autonomous: "wolf" };

/** What a tier adds over the one before it, so the cards read as a progression rather than three lists. */
function tierCapabilities(bundle: Bundle, previous: Bundle | undefined, rows: BundlesData["rows"]) {
  const label = (slug: string) => rows.find((row) => row.slug === slug)?.label ?? slug;
  const added = previous ? bundle.includes.filter((slug) => !previous.includes.includes(slug)) : bundle.includes;
  const shown = added.slice(0, 3).map(label);
  const remaining = added.length - shown.length;
  return { lead: previous ? `Everything in ${previous.name}, plus` : null, shown, remaining };
}

function Chevron() {
  return (
    <span aria-hidden="true" className="hidden items-center justify-center text-slate/60 lg:flex">
      <svg viewBox="0 0 16 16" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6"><path strokeLinecap="round" strokeLinejoin="round" d="m6 3 5 5-5 5" /></svg>
    </span>
  );
}

type BundleTierCardsProps = {
  data: BundlesData;
  selectedId: string;
  onSelect: (id: string) => void;
  error?: string;
};

export function BundleTierCards({ data, selectedId, onSelect, error }: BundleTierCardsProps) {
  const columns = data.bundles.map(() => "minmax(0,1fr)").join(" 1.25rem ");
  return (
    <fieldset aria-describedby={error ? "edge-bundle-error" : "edge-bundle-hint"}>
      <legend className="sr-only">Choose your Edge bundle</legend>
      <p id="edge-bundle-hint" className="mb-4 text-sm leading-6 text-slate">Each tier builds on the one before it. You can change tiers with your Edge specialist before anything is finalized.</p>
      <div className="grid gap-4 lg:[grid-template-columns:var(--tier-columns)]" style={{ "--tier-columns": columns } as CSSProperties}>
          {data.bundles.map((bundle, index) => {
            const previous = data.bundles[index - 1];
            const capabilities = tierCapabilities(bundle, previous, data.rows);
            const guidance = BUNDLE_GUIDANCE[bundle.id];
            const isSelected = bundle.id === selectedId;
            const tierMark = TIER_MARKS[bundle.id];
            return (
              <div key={bundle.id} className="contents">
                {index > 0 ? <Chevron /> : null}
                <label className="group relative block h-full cursor-pointer">
                  <input
                    className="peer sr-only"
                    type="radio"
                    name="bundle"
                    value={bundle.id}
                    checked={isSelected}
                    onChange={() => onSelect(bundle.id)}
                    required
                    aria-describedby={`edge-bundle-${bundle.id}-detail`}
                  />
                  {bundle.highlight ? (
                    <span className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-signal bg-paper-raised px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-signal-ink">
                      Recommended
                    </span>
                  ) : null}
                  <span className="flex h-full flex-col rounded-[10px] border border-hairline bg-paper-raised p-5 transition-[border-color,background-color,box-shadow] duration-150 hover:border-slate/40 peer-checked:border-ink peer-checked:bg-accent-tint peer-checked:shadow-[inset_0_0_0_1px_var(--color-ink)] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-3 peer-focus-visible:outline-ink">
                    <span className="flex h-6 flex-nowrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate">
                      <span className="min-w-0 truncate">Tier {index + 1}{guidance ? ` · ${guidance.stage}` : ""}</span>
                      {tierMark ? <span aria-hidden="true" className="ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-accent-soft text-ink group-has-checked:hidden"><ModuleIcon name={tierMark} className="size-4" /></span> : null}
                      <span className="ml-auto hidden size-6 shrink-0 items-center justify-center rounded-full bg-ink text-paper-raised group-has-checked:inline-flex">
                        <svg aria-hidden="true" viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="m3 8.5 3 3 7-7" /></svg>
                        <span className="sr-only">Selected</span>
                      </span>
                    </span>
                    <span className="mt-3 block font-display text-lg font-semibold leading-tight tracking-tight text-ink">{bundle.name}</span>
                    <span className="mt-1.5 block text-sm leading-6 text-slate-strong">{bundle.tagline}</span>
                    <span id={`edge-bundle-${bundle.id}-detail`} className="mt-4 block border-t border-hairline pt-4">
                      {capabilities.lead ? <span className="block text-xs font-medium text-slate">{capabilities.lead}</span> : <span className="block text-xs font-medium text-slate">Included modules</span>}
                      <span className="mt-2 block space-y-1.5">
                        {capabilities.shown.map((item) => (
                          <span key={item} className="flex items-start gap-2 text-sm text-ink">
                            <svg aria-hidden="true" viewBox="0 0 16 16" className="mt-1 size-3.5 shrink-0 text-ink" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="m3 8.5 3 3 7-7" /></svg>
                            {item}
                          </span>
                        ))}
                        {capabilities.remaining > 0 ? <span className="block pl-[1.375rem] text-xs text-slate">+ {capabilities.remaining} more</span> : null}
                      </span>
                    </span>
                  </span>
                </label>
              </div>
            );
          })}
      </div>
      <FieldError id="edge-bundle-error" message={error} />
    </fieldset>
  );
}
